import { MainSectionWithGaps } from '../../../../components/sections/MainSection';
import strings from '../../../../strings/strings.json';
import { useLocation, useParams } from 'react-router-dom';
import useGetRequest from '../../../../hooks/useGetRequest';
import { ActivityMeeting, LocationType } from '../../../../models/ActivityMeeting';
import styled from 'styled-components';
import { sizes, theme } from '../../../../theme';
import { useState } from 'react';
import Notification, { NotificationType } from '../../../../components/notifiers/Notification';
import { IconSize } from 'assets/icons/icon-sizes';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import { TabDefinition } from 'components/tabs/Tabs';
import FileIcon from 'assets/icons/other/FileIcon';
import Attendance from './components/Attendance';
import VideoIcon from 'assets/icons/pathway/VideoIcon';
import UploadVideo from './components/UploadVideo';
import { FlexColAlignCenter, FlexColLg, Headline3 } from 'components/Elements';
import NavBackButton from 'components/buttons/NavBackButton';
import Header from 'components/sections/Header';
import PreviewIcon from 'assets/icons/controls/PreviewIcon';
import PresentationSlidesSection from './PresentationSlidesSection';
import { ButtonDetails, DateTimeLocation } from 'pages/learner/activity/components/live-training/LearnerLiveTraining';

type State = {
    activeTabId: string;
};

type PageState = 'detail' | 'present';

export default function LiveTrainingDetailPage(): JSX.Element {
    const { state } = useLocation();
    const { activeTabId } = (state as State) ?? {};
    const { courseId, liveTrainingId } = useParams();

    const [pageState, setPageState] = useState<PageState>('detail');

    const {
        data: liveTraining,
        loading,
        errors,
        refetchData,
    } = useGetRequest<ActivityMeeting>(
        `courses/schedule/${courseId}/live-training/${liveTrainingId}`,
        !liveTrainingId,
    );

    if (!liveTraining) return <></>;
    if (loading) return <></>;
    if (errors && errors.length > 0) {
        return (
            <MainSectionWithGaps>
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.liveTrainingDetailPage.titleError}
                    description={strings.errorMessages.genericDetail}
                    big
                />
            </MainSectionWithGaps>
        );
    }

    const attendanceTab: TabDefinition = {
        id: 'attendance-tab',
        name: strings.liveTrainingDetailPage.attendanceTab.title,
        icon: FileIcon,
        content: <Attendance />,
    };

    const uploadVideoTab: TabDefinition = {
        id: 'upload-video-tab',
        name: strings.liveTrainingDetailPage.uploadVideoTab.title,
        icon: VideoIcon,
        content: (
            <UploadVideo
                activityId={liveTraining.activityId}
                scheduledCourseId={liveTraining.scheduledCourseId}
                liveTrainingId={liveTraining.id}
                s3FilePath={liveTraining.videoDocument?.s3Filepath}
                refetchData={refetchData}
            />
        ),
    };

    if (pageState === 'present') {
        return (
            <PageContent>
                <Header
                    title={liveTraining.courseTitle}
                    aboveTitleElement={
                        <NavBackButton
                            backButtonTitle={strings.liveTrainingDetailPage.backButton.title}
                            backButtonAria={strings.liveTrainingDetailPage.backButton.aria}
                            backButtonHref={''}
                            buttonFunctionOverride={() => setPageState('detail')}
                        />
                    }
                />
                <MainContent>
                    <Headline3>{liveTraining.activity.title}</Headline3>
                    <PresentationSlidesSection
                        slides={liveTraining.presentationSlides.map((ps) => {
                            return {
                                title: ps.title,
                                content: ps.content,
                                image: ps.imageDocuments.length ? ps.imageDocuments[0] : undefined,
                                order: ps.order,
                            };
                        })}
                    />
                </MainContent>
            </PageContent>
        );
    }

    const buttonDetails: ButtonDetails[] = [
        {
            title: strings.liveTrainingPage.presentationButton,
            aria: strings.liveTrainingPage.presentationButtonAria,
            onClick: () => setPageState('present'),
            icon: <PreviewIcon colour={theme.cardBackground} size={IconSize.MEDIUM} />,
        },
    ];

    if (liveTraining.locationType === LocationType.ONLINE) {
        buttonDetails.push({
            title: strings.liveTrainingPage.joinMeetingButton,
            aria: strings.liveTrainingPage.joinMeetingButtonAria,
            onClick: () => window.open(liveTraining.joinLink),
            icon: <></>,
        });
    }

    return (
        <>
            <PageContent>
                <Header
                    title={liveTraining.courseTitle}
                    aboveTitleElement={
                        <NavBackButton
                            backButtonTitle={strings.liveTrainingDetailPage.backButton.title}
                            backButtonAria={strings.liveTrainingDetailPage.backButton.aria}
                            backButtonHref={`/live-course/${courseId}`}
                            backButtonNavigateState={{ state: { activeTabId: 'live-training' } }}
                        />
                    }
                    roleSwitch={false}
                />
                <MainContent>
                    <Headline3>{liveTraining.activity.title}</Headline3>
                    <DateTimeLocation
                        meetingDate={liveTraining.meetingDatetime}
                        durationInMins={liveTraining.durationInMins}
                        locationType={liveTraining.locationType}
                        location={liveTraining.location}
                        buttons={buttonDetails}
                    />
                    <TabStructureComponent tabs={[attendanceTab, uploadVideoTab]} initialActiveTabId={activeTabId} />
                </MainContent>
            </PageContent>
        </>
    );
}

const PageContent = styled(FlexColAlignCenter)`
    width: 100%;
    min-height: calc(100vh - 184px);
`;

const MainContent = styled(FlexColLg)`
    width: 100%;
    height: 100%;
    max-width: ${sizes.appMaxWidthWithoutPadding}px;
    padding: ${sizes.spacingLg} ${sizes.edgePadding};
`;
